import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import type { FC, ReactNode, MouseEvent } from 'react';
import Card from './ui/Card';
import Popover from './ui/Popover';
import AvailabilityOverlay from './AvailabilityOverlay'; // Import the overlay
import AvailabilityTransparentOverlays from './AvailabilityTransparentOverlays'; // Import the new component
import HolidayOverlay from './HolidayOverlay'; // Import the holiday overlay
import { parseTime, formatTimeFromMinutes, timeToMinutes, formatDayWithNumber, getWeekStartDate } from '../utils/helpers';
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE, TOTAL_SCHEDULE_MINUTES, timeSlots, DRAG_CONFIG } from '../config/constants';
import { useActiveDays } from '../hooks/useActiveDays';
import ScheduleItemCard from './ScheduleItemCard';
import ScheduleEntry from '../models/ScheduleEntry'; // Import the ScheduleEntry class
import { availabilityService, Severity } from '../services/AvailabilityService';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import Room from '../models/Room';
import { getWeekDateRange } from '../utils/timeUtils'; // Import getWeekDateRange
import ScheduleEntryDetailView from './ScheduleEntryDetailView';
import { useDrop } from 'react-dnd';
import type { Holiday } from '../services/AcademicYearService';
import type { DraggedScheduleEntry } from '../types/dragAndDrop';
import { useSharedScroll } from '../contexts/SharedScrollContext';
import { useScheduleResize } from '../hooks/useScheduleResize';
import ResizeTooltip from './ResizeTooltip';
import ResizeGridGuides from './ResizeGridGuides';

// Define drag and drop item types
const ItemTypes = {
    COURSE: 'course',
    SCHEDULED_ITEM: 'scheduled_item'
};

// Define types for the component
interface AvailabilityStatus {
    teacherStatus: Severity;
    groupStatus: Severity;
    isRoomOccupied: boolean;
    isRoomValidForSubject: boolean;
    overallStatus: Severity;
    conflictReasons: Array<{ reason: string; severity: Severity }>;
    conflictReason?: string;
}

// DraggedItemInfo interface removed - now using DraggedScheduleEntry from types/dragAndDrop

interface DragOverData {
    roomIndex: number;
    targetTimeMinutes: number;
    availabilityStatus: AvailabilityStatus;
}

interface PopoverState {
    itemId: string | null;
    reasons: Array<{ reason: string; severity: string }>;
    position: { x: number; y: number };
}

interface OverlayInfo {
    isVisible: boolean;
    reasons: Array<{ reason: string; severity: Severity }>;
    position: { top: number; left: number };
}

interface TimeLabel {
    time: string;
    row: number;
}

interface DayInfo {
    shortName: string;
    dayNumber: number | null;
    fullDate: Date | null;
}

interface DayTableSimpleProps {
    day: string;
    rooms: Room[];
    items: ScheduleEntry[];
    dayScheduleEntry: ScheduleEntry[];
    user: { role: string };
    onOpenModal: (item: ScheduleEntry) => void;
    onItemDrop: (itemId: string, roomId: number, day: string, startTime: string) => void;
    onItemResize?: (itemId: string, newStartTime: string, newEndTime: string) => void;
    draggedItemInfo: DraggedScheduleEntry;
    onGlobalDragStart: (scheduleEntry: ScheduleEntry, clickOffsetY: number) => void;
    onGlobalDragEnd: () => void;
    availabilityOverlayData?: Map<string, AvailabilityStatus>;
    KEY_SEPARATOR: string;
    dayHeaderRef?: (element: HTMLElement | null) => void;
    teachers: Teacher[];
    groups: Group[];
    subjects: Subject[];
    currentWeekNumber?: number;
    currentYear?: number;
    dragToCreateData?: {
        isActive: boolean;
        startTime: number | null;
        endTime: number | null;
        roomId: number | null;
        day: string | null;
        startPosition: { x: number; y: number };
        currentPosition: { x: number; y: number };
    };
    onDragToCreateStart?: (day: string, roomId: number, startTimeMinutes: number, startPosition: { x: number; y: number }, currentWeekNumber?: number, currentYear?: number) => void;
    onDragToCreateMove?: (currentPosition: { x: number; y: number }, currentTimeMinutes: number) => void;
    onDragToCreateEnd?: () => void;
    onDragToCreateCancel?: () => void;
    holiday?: Holiday | null;
    // Context menu handlers
    onCopyItem?: (item: ScheduleEntry) => void;
    onDuplicateItem?: (item: ScheduleEntry) => void;
    onDelete?: (item: ScheduleEntry) => void;
    showMondayHeader?: boolean;
    isSaveAnimationActive?: boolean;
    highlightedItemId?: string | number | null;
}

// Add proper style type
interface ItemStyle {
    display?: string;
    position?: 'absolute';
    top?: string;
    height?: string;
    gridColumnStart?: number;
    gridColumnEnd?: number;
    width?: string;
    zIndex?: number;
    minHeight?: string;
    left?: string;
}

/**
 * Renders a schedule grid for a single day (Wide View).
 * Features: Item positioning, click modal, drag & drop with availability highlighting.
 */
const DayTableSimple: FC<DayTableSimpleProps> = ({
    day,
    rooms,
    items,
    dayScheduleEntry,
    user,
    onOpenModal,
    onItemDrop,
    onItemResize,
    draggedItemInfo,
    onGlobalDragStart,
    onGlobalDragEnd,
    availabilityOverlayData,
    KEY_SEPARATOR,
    dayHeaderRef,
    teachers,
    groups,
    subjects,
    currentWeekNumber,
    currentYear,
    dragToCreateData,
    onDragToCreateStart,
    onDragToCreateMove,
    onDragToCreateEnd,
    onDragToCreateCancel,
    holiday,
    onCopyItem,
    onDuplicateItem,
    onDelete,
    showMondayHeader,
    isSaveAnimationActive = false,
    highlightedItemId = null
}) => {
    // Get active days from AssetManager
    const { activeDays } = useActiveDays();

    // Shared scroll context
    const { sharedScrollLeft, registerScrollRef, unregisterScrollRef, updateSharedScroll } = useSharedScroll();

    // Local State
    const [dragOverData, setDragOverData] = useState<DragOverData | null>(null);
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [activePopover, setActivePopover] = useState<PopoverState>({
        itemId: null,
        reasons: [] as Array<{ reason: string; severity: string }>,
        position: { x: 0, y: 0 }
    });
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [overlayInfo, setOverlayInfo] = useState<OverlayInfo>({
        isVisible: false,
        reasons: [] as Array<{ reason: string; severity: Severity }>,
        position: { top: 0, left: 0 }
    });

    // Public read-only detail modal state
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [detailEntry, setDetailEntry] = useState<ScheduleEntry | null>(null);
    const dayHeaderElementRef = useRef<HTMLHeadingElement | null>(null);

    // Add drag-to-create local state
    const [isDraggingToCreate, setIsDraggingToCreate] = useState(false);
    const [dragStartPosition, setDragStartPosition] = useState<{ x: number; y: number } | null>(null);
    const [dragStartData, setDragStartData] = useState<{
        day: string;
        room: Room;
        startTimeMinutes: number;
        position: { x: number; y: number };
    } | null>(null);

    // Add resize visual feedback state (Requirement 5.2, 5.3, 5.4)
    const [resizeTooltip, setResizeTooltip] = useState<{
        visible: boolean;
        position: { x: number; y: number };
        violations: string[];
    }>({
        visible: false,
        position: { x: 0, y: 0 },
        violations: []
    });

    // Flag to prevent clicks immediately after resize completion
    const recentlyResizedRef = useRef<boolean>(false);

    // Performance optimization ref for drag over throttling
    const dragOverThrottleRef = useRef<{ roomIndex: number; targetTimeMinutes: number } | null>(null);

    // Constants for drag detection
    const DRAG_THRESHOLD = DRAG_CONFIG.dragThreshold; // pixels - minimum movement to start drag

    // Initialize resize functionality
    const {
        resizeState,
        handlers: resizeHandlers,
        handleKeyDown,
        handleKeyUp
    } = useScheduleResize();

    // Convert items to ScheduleEntry objects
    const scheduleEntries = useMemo(() => {
        // items should already be ScheduleEntry objects, but ensure they are
        return items.map(item => item instanceof ScheduleEntry ? item : ScheduleEntry.fromObject(item));
    }, [items]);

    // dayScheduleEntry should already be ScheduleEntry objects
    const baseScheduleEntries = useMemo(() => {
        return dayScheduleEntry || [];
    }, [dayScheduleEntry]);

    const isAdmin = user?.role === 'Admin';

    // Constants for layout calculation
    const checkIntervalMinutes = 60;
    const timeIntervalMinutes = 30;
    const numberOfTimeRows = Math.ceil(TOTAL_SCHEDULE_MINUTES / timeIntervalMinutes);
    const rowHeightPx = 40;
    const gridHeaderHeight = 40;
    const gridTimeColWidth = 60;
    const gridPadding = 8; // p-2 class adds 8px padding
    const totalGridContentHeight = numberOfTimeRows * rowHeightPx;

    // Memoize room index lookup - map room names to indices for availability overlays
    const roomIndexMap = useMemo(() => {
        const map = new Map();
        rooms.forEach((room, index) => {
            const roomName = room.name || room.roomCode || `Room ${room.id}`;
            map.set(roomName, index);
        });
        return map;
    }, [rooms]);

    // Add react-dnd drop functionality
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: [ItemTypes.COURSE, ItemTypes.SCHEDULED_ITEM], // Handle both templates and existing items
        hover: (item: any, monitor) => {
            if (!isAdmin || !draggedItemInfo.entry || !baseScheduleEntries || !gridRef.current) return;

            // Get the hover coordinates
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;

            const gridRect = gridRef.current.getBoundingClientRect();
            const cursorX = clientOffset.x - gridRect.left;
            const cursorY = clientOffset.y - gridRect.top;

            // --- Calculate target room (same logic as drop) ---
            const minVisualColumnWidth = 120;
            const scrollLeft = gridRef.current.scrollLeft || 0;
            const cursorXInContent = cursorX + scrollLeft;
            const visibleRoomAreaWidth = gridRect.width - gridTimeColWidth;
            const averageDynamicWidthIfViewportConstrained = visibleRoomAreaWidth > 0 ? visibleRoomAreaWidth / rooms.length : 1;
            const effectiveRoomColumnWidth = Math.max(minVisualColumnWidth, averageDynamicWidthIfViewportConstrained);
            let targetRoomIndex = Math.floor((cursorXInContent - gridTimeColWidth) / effectiveRoomColumnWidth);
            targetRoomIndex = Math.max(0, Math.min(targetRoomIndex, rooms.length - 1));
            const targetRoom = rooms[targetRoomIndex];

            // --- Calculate target time (same logic as drop) ---
            const contentCursorY = Math.max(0, cursorY - gridHeaderHeight);
            const contentClickOffsetY = draggedItemInfo.clickOffsetY || 0;
            const targetTopPx = Math.max(0, contentCursorY - contentClickOffsetY);
            const minutesOffset = (targetTopPx / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
            let targetTimeMinutes = SCHEDULE_START_MINUTE + minutesOffset;
            const minStartTime = SCHEDULE_START_MINUTE;
            const maxStartTime = SCHEDULE_END_MINUTE - (draggedItemInfo.durationMinutes || 0);
            targetTimeMinutes = Math.max(minStartTime, Math.min(targetTimeMinutes, maxStartTime));
            const roundingStep = DRAG_CONFIG.timeSnapIntervalMinutes;
            targetTimeMinutes = Math.round(targetTimeMinutes / roundingStep) * roundingStep;

            // Performance optimization: Only update if position changed significantly
            const hasSignificantChange = !dragOverThrottleRef.current ||
                Math.abs(dragOverThrottleRef.current.roomIndex - targetRoomIndex) > 0 ||
                Math.abs(dragOverThrottleRef.current.targetTimeMinutes - targetTimeMinutes) > 2; // 2 minute threshold (smaller than configured rounding)

            if (!hasSignificantChange) {
                return; // Skip processing if position hasn't changed much
            }

            // Update the ref with current values
            dragOverThrottleRef.current = { roomIndex: targetRoomIndex, targetTimeMinutes };

            // Use the dragged entry directly
            const draggedEntry = draggedItemInfo.entry;

            let availabilityStatus: AvailabilityStatus = {
                teacherStatus: 'red',
                groupStatus: 'red',
                isRoomOccupied: false,
                isRoomValidForSubject: false,
                overallStatus: 'red',
                conflictReasons: [{ reason: 'Dragged item not found.', severity: 'red' }],
                conflictReason: 'Dragged item not found.'
            };

            if (draggedEntry && targetRoom) {
                try {
                    const targetRoomName = targetRoom.name || targetRoom.roomCode || `Room ${targetRoom.id}`;
                    const result = availabilityService.checkAvailability(
                        draggedEntry, targetRoomName, day, targetTimeMinutes,
                        baseScheduleEntries, teachers, groups, subjects
                    );
                    availabilityStatus = {
                        ...result,
                        teacherStatus: result.teacherStatus as Severity,
                        groupStatus: result.groupStatus as Severity,
                        overallStatus: result.overallStatus as Severity,
                        conflictReasons: result.conflictReasons.map(reason => ({
                            ...reason,
                            severity: reason.severity as Severity
                        }))
                    };
                } catch (error) {
                    console.error(`[${day}-Simple] Error checking availability during hover:`, error);
                }
            }

            // Update the drag over data to show the overlay
            setDragOverData({
                roomIndex: targetRoomIndex,
                targetTimeMinutes,
                availabilityStatus
            });
        },
        drop: (item: any, monitor) => {
            console.log(`[${day}-Simple] REACT-DND DROP SUCCESS!`, item);

            if (!isAdmin || !gridRef.current) {
                console.log(`[${day}-Simple] Not admin or no grid ref`);
                return;
            }

            // Get the drop coordinates
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) {
                console.log(`[${day}-Simple] No client offset`);
                return;
            }

            const gridRect = gridRef.current.getBoundingClientRect();
            const relativeX = clientOffset.x - gridRect.left;
            const relativeY = clientOffset.y - gridRect.top;

            console.log(`[${day}-Simple] Drop coordinates:`, { relativeX, relativeY });

            // Calculate target room
            const minVisualColumnWidth = 120;
            const scrollLeft = gridRef.current.scrollLeft || 0;
            const cursorXInContent = relativeX + scrollLeft;
            const visibleRoomAreaWidth = gridRect.width - gridTimeColWidth;
            const averageDynamicWidthIfViewportConstrained = visibleRoomAreaWidth > 0 ? visibleRoomAreaWidth / rooms.length : 1;
            const effectiveRoomColumnWidth = Math.max(minVisualColumnWidth, averageDynamicWidthIfViewportConstrained);
            let targetRoomIndex = Math.floor((cursorXInContent - gridTimeColWidth) / effectiveRoomColumnWidth);
            targetRoomIndex = Math.max(0, Math.min(targetRoomIndex, rooms.length - 1));
            const targetRoom = rooms[targetRoomIndex];

            // Calculate target time
            const contentCursorY = Math.max(0, relativeY - gridHeaderHeight);
            const clickOffsetY = draggedItemInfo.clickOffsetY || 0;
            const targetTopPx = Math.max(0, contentCursorY - clickOffsetY);
            const minutesOffset = (targetTopPx / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
            let targetTimeMinutes = SCHEDULE_START_MINUTE + minutesOffset;
            const minStartTime = SCHEDULE_START_MINUTE;
            const maxStartTime = SCHEDULE_END_MINUTE - (draggedItemInfo.durationMinutes || 0);
            targetTimeMinutes = Math.max(minStartTime, Math.min(targetTimeMinutes, maxStartTime));
            const roundingStep = DRAG_CONFIG.timeSnapIntervalMinutes;
            targetTimeMinutes = Math.round(targetTimeMinutes / roundingStep) * roundingStep;
            const targetTimeString = formatTimeFromMinutes(targetTimeMinutes);

            // Add null check for targetRoom
            if (!targetRoom) {
                console.error(`[${day}-Simple] No target room found for index ${targetRoomIndex}`);
                return;
            }

            const targetRoomName = targetRoom.name || targetRoom.roomCode || `Room ${targetRoom.id}`;
            console.log(`[${day}-Simple] Drop target: Room=${targetRoomName}, Time=${targetTimeString}`);

            // Call the drop handler
            if (onItemDrop && draggedItemInfo.entry?.id && targetRoom) {
                onItemDrop(draggedItemInfo.entry.id, targetRoom.id, day, targetTimeString);
            } else {
                console.log(`[${day}-Simple] Cannot drop: missing required data`, {
                    hasDropHandler: !!onItemDrop,
                    draggedItemId: draggedItemInfo.entry?.id,
                    targetRoom,
                    targetTimeString
                });
            }
        },
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop(),
        }),
        end: () => {
            // Clear the drag over data when drag ends
            setDragOverData(null);
            dragOverThrottleRef.current = null;
        }
    }), [isAdmin, day, rooms, onItemDrop, draggedItemInfo, gridTimeColWidth, gridHeaderHeight, totalGridContentHeight, baseScheduleEntries, teachers, groups, subjects, currentWeekNumber, currentYear]);

    // Combine the drop ref with the grid ref
    const combinedRef = useCallback((node: HTMLDivElement | null) => {
        gridRef.current = node;
        drop(node);

        // Register/unregister with shared scroll context
        if (node) {
            registerScrollRef(node);
        } else {
            unregisterScrollRef(gridRef.current);
        }
    }, [drop, registerScrollRef, unregisterScrollRef]);

    // Helper function to convert group IDs to display names
    const getGroupDisplayNames = useCallback((entryGroups: (number | string | { id: number; name?: string; group_code?: string })[]) => {
        if (!entryGroups || entryGroups.length === 0) return 'All Groups';

        return entryGroups.map(group => {
            // If it's already an object with name/code, use that
            if (typeof group === 'object' && group !== null) {
                return group.name || group.group_code || group.id.toString();
            }

            // If it's an ID (number or string), look it up in the groups prop
            const foundGroup = groups.find(g => g.id === group || g.id === Number(group));
            if (foundGroup) {
                return foundGroup.name || foundGroup.group_code || foundGroup.id?.toString() || 'Unknown';
            }

            // Fallback to the raw value
            return group.toString();
        }).join(', ');
    }, [groups]);

    // useEffect(() => {
    //     console.log('DayTable received new items:', items);
    // }, [items]);

    // --- Static Item Conflict Check & Popover Logic (Keep as is) ---
    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node) && !(event.target as Element).closest('.conflict-icon-span')) {
                setActivePopover({ itemId: null, reasons: [], position: { x: 0, y: 0 } });
            }
        };
        if (activePopover.itemId) { document.addEventListener('mousedown', handleClickOutside); }
        else { document.removeEventListener('mousedown', handleClickOutside); }
        return () => { document.removeEventListener('mousedown', handleClickOutside); };
    }, [activePopover.itemId]);

    const handleIconClick = (e: React.MouseEvent, currentItem: ScheduleEntry, reasonsArray: Array<{ reason: string; severity: string }>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const posX = rect.left + window.scrollX;
        const posY = rect.bottom + window.scrollY + 5;
        if (activePopover.itemId === currentItem.id) {
            setActivePopover({ itemId: null, reasons: [], position: { x: 0, y: 0 } });
        } else {
            setActivePopover({ itemId: currentItem.id, reasons: reasonsArray, position: { x: posX, y: posY } });
        }
    };

    // --- Helper function to detect overlapping items ---
    const getOverlappingGroups = useCallback((entries: ScheduleEntry[]) => {
        const groups: ScheduleEntry[][] = [];

        for (const entry of entries) {
            if (!entry || !entry.room || !entry.startMinutes || !entry.durationMinutes) continue;

            // Find if this entry overlaps with any existing group
            let addedToGroup = false;

            for (const group of groups) {
                // Check if this entry overlaps with any item in this group
                const overlapsWithGroup = group.some(groupEntry => {
                    return (
                        groupEntry.room === entry.room &&
                        groupEntry.day === entry.day &&
                        groupEntry.startMinutes !== null &&
                        groupEntry.durationMinutes !== null &&
                        entry.startMinutes !== null &&
                        entry.durationMinutes !== null &&
                        // Check time overlap: two items overlap if one starts before the other ends
                        groupEntry.startMinutes < (entry.startMinutes + entry.durationMinutes) &&
                        entry.startMinutes < (groupEntry.startMinutes + groupEntry.durationMinutes)
                    );
                });

                if (overlapsWithGroup) {
                    group.push(entry);
                    addedToGroup = true;
                    break;
                }
            }

            // If not added to any existing group, create a new group
            if (!addedToGroup) {
                groups.push([entry]);
            }
        }

        return groups;
    }, []);

    // --- Memoized overlapping groups calculation ---
    const overlappingGroups = useMemo(() => {
        return getOverlappingGroups(scheduleEntries);
    }, [scheduleEntries, getOverlappingGroups]);

    // --- Enhanced Item Style Calculation with overlap handling and resize support ---
    const getItemStyle = useCallback((entry: ScheduleEntry): ItemStyle => {
        if (!entry || !entry.timeStart || !entry.timeEnd || !entry.room) return { display: 'none' };
        const roomIndex = roomIndexMap.get(entry.room);
        if (roomIndex === undefined) return { display: 'none' };

        // Use resize state for dynamic updates during resize (Requirement 5.1)
        let startMinutes = entry.startMinutes;
        let durationMinutes = entry.durationMinutes;

        // If this entry is being resized, use the current resize state for real-time updates
        if (resizeState.isResizing && resizeState.itemId === entry.id) {
            const currentStartMinutes = timeToMinutes(resizeState.currentStartTime);
            const currentEndMinutes = timeToMinutes(resizeState.currentEndTime);

            if (currentStartMinutes !== null && currentEndMinutes !== null) {
                startMinutes = currentStartMinutes;
                durationMinutes = currentEndMinutes - currentStartMinutes;
            }
        }

        if (startMinutes === null || durationMinutes === null || durationMinutes <= 0 || TOTAL_SCHEDULE_MINUTES <= 0) {
            return { display: 'none' };
        }

        // Find the overlapping group this entry belongs to
        const overlappingGroup = overlappingGroups.find(group => group.includes(entry));
        const isOverlapping = overlappingGroup && overlappingGroup.length > 1;

        const offsetMinutes = startMinutes - SCHEDULE_START_MINUTE;
        const clampedOffsetMinutes = Math.max(0, offsetMinutes);
        const topCalc = `${((clampedOffsetMinutes / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight + gridPadding}px`;
        const maxPossibleDuration = TOTAL_SCHEDULE_MINUTES - clampedOffsetMinutes;
        const clampedDurationMinutes = Math.min(durationMinutes, maxPossibleDuration);
        const heightCalc = `calc(${totalGridContentHeight}px * ${clampedDurationMinutes / TOTAL_SCHEDULE_MINUTES})`;
        const gridColumnStart = roomIndex + 2;

        let width = '100%';
        let left = '0%';

        if (isOverlapping) {
            // Calculate position within the overlapping group
            const groupSize = overlappingGroup.length;
            const indexInGroup = overlappingGroup.indexOf(entry);

            // Each item takes up 1/groupSize of the width
            const itemWidthPercent = 100 / groupSize;
            width = `${itemWidthPercent}%`;
            left = `${indexInGroup * itemWidthPercent}%`;
        }

        // Add smooth transitions for resize operations (Requirement 5.5)
        const style: ItemStyle = {
            position: 'absolute',
            top: topCalc,
            height: heightCalc,
            left: left,
            gridColumnStart,
            gridColumnEnd: gridColumnStart + 1,
            width: width,
            zIndex: resizeState.isResizing && resizeState.itemId === entry.id ? 20 : 10, // Higher z-index during resize
            minHeight: '10px'
        };

        return style;
    }, [roomIndexMap, day, totalGridContentHeight, gridHeaderHeight, overlappingGroups, resizeState]);

    // --- Drag and Drop Handlers ---
    const handleItemDragStart = useCallback((e: React.DragEvent, entry: ScheduleEntry) => {
        // Prevent drag if clicking the conflict icon
        if ((e.target as Element).closest('.conflict-icon-span')) { e.preventDefault(); return; }
        if (!isAdmin || !entry) { e.preventDefault(); return; }

        // Prevent drag-and-drop when in resize mode (Requirement 2.4, 13.1)
        if (resizeState.isResizing) {
            e.preventDefault();
            return;
        }

        console.log('DayTableSimple: Starting drag for item:', entry.id);

        const clickOffsetY = (e.nativeEvent as any).offsetY || 0;
        onGlobalDragStart(entry, clickOffsetY); // Pass ScheduleEntry directly to parent handler

        // Set the drag data for HTML5 DnD
        e.dataTransfer.setData('text/plain', entry.id);

        // The application/json format might not be accessible directly in React DnD
        // but setting it helps with debugging
        e.dataTransfer.setData('application/json', JSON.stringify({
            id: entry.id,
            typeDnd: ItemTypes.SCHEDULED_ITEM
        }));
        e.dataTransfer.effectAllowed = 'move';

        if (e.currentTarget && 'style' in e.currentTarget) {
            (e.currentTarget as HTMLElement).style.opacity = '0.5';
            (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
        }
    }, [isAdmin, onGlobalDragStart]);

    // Use the global drag end handler passed from the parent
    const handleItemDragEnd = useCallback((e: React.DragEvent) => {
        if (!isAdmin) return;

        // Don't process drag end if in resize mode
        if (resizeState.isResizing) return;

        if (e.currentTarget && 'style' in e.currentTarget) {
            (e.currentTarget as HTMLElement).style.opacity = '1';
            (e.currentTarget as HTMLElement).style.cursor = 'grab';
        }
        onGlobalDragEnd(); // Call parent handler
        setDragOverData(null); // Clear local state
        setOverlayInfo({ isVisible: false, reasons: [], position: { top: 0, left: 0 } });
        dragOverThrottleRef.current = null; // Reset throttle for next drag
    }, [isAdmin, onGlobalDragEnd, resizeState.isResizing]);

    const handleGridDragOver = useCallback((e: React.DragEvent) => {
        if (!isAdmin || !draggedItemInfo.entry || !baseScheduleEntries) return;

        // Prevent drag over processing when in resize mode
        if (resizeState.isResizing) return;

        // CRITICAL: Always prevent default to allow dropping
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (!gridRef.current) return;

        const gridRect = gridRef.current.getBoundingClientRect();
        const cursorX = e.clientX - gridRect.left;
        const cursorY = e.clientY - gridRect.top;

        // --- Start of Corrected Logic ---
        const minVisualColumnWidth = 120; // The '120px' from minmax(120px, 1fr) in the DayTableSimple CSS
        const scrollLeft = gridRef.current.scrollLeft || 0;
        const cursorXInContent = cursorX + scrollLeft; // Cursor X relative to the start of the scrollable content

        // Calculate the width of the area dedicated to room columns within the viewport
        const visibleRoomAreaWidth = gridRect.width - gridTimeColWidth;

        // Calculate the average width of a column if it were purely dynamic based on visible width
        const averageDynamicWidthIfViewportConstrained = visibleRoomAreaWidth > 0 ? visibleRoomAreaWidth / rooms.length : 1;

        // Determine the effective column width to use for hit detection
        // This aims to use 100px if the average would be less, otherwise use the larger average (when grid is very wide)
        const effectiveRoomColumnWidth = Math.max(minVisualColumnWidth, averageDynamicWidthIfViewportConstrained);

        let targetRoomIndex = Math.floor((cursorXInContent - gridTimeColWidth) / effectiveRoomColumnWidth);
        targetRoomIndex = Math.max(0, Math.min(targetRoomIndex, rooms.length - 1));
        const targetRoom = rooms[targetRoomIndex];
        // --- End of Corrected Logic for targetRoomIndex ---

        // Calculate Target Time Minutes (Vertical logic remains the same)
        const contentCursorY = Math.max(0, cursorY - gridHeaderHeight);
        const contentClickOffsetY = draggedItemInfo.clickOffsetY || 0;
        const targetTopPx = Math.max(0, contentCursorY - contentClickOffsetY);
        const minutesOffset = (targetTopPx / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
        let targetTimeMinutes = SCHEDULE_START_MINUTE + minutesOffset;
        const minStartTime = SCHEDULE_START_MINUTE;
        const maxStartTime = SCHEDULE_END_MINUTE - (draggedItemInfo.durationMinutes || 0);
        targetTimeMinutes = Math.max(minStartTime, Math.min(targetTimeMinutes, maxStartTime));
        const roundingStep = DRAG_CONFIG.timeSnapIntervalMinutes;
        targetTimeMinutes = Math.round(targetTimeMinutes / roundingStep) * roundingStep;

        // Performance optimization: Only update if position changed significantly
        const hasSignificantChange = !dragOverThrottleRef.current ||
            Math.abs(dragOverThrottleRef.current.roomIndex - targetRoomIndex) > 0 ||
            Math.abs(dragOverThrottleRef.current.targetTimeMinutes - targetTimeMinutes) > 2; // 2 minute threshold (smaller than configured rounding)

        if (!hasSignificantChange) {
            return; // Skip processing if position hasn't changed much
        }

        // Update the ref with current values
        dragOverThrottleRef.current = { roomIndex: targetRoomIndex, targetTimeMinutes };

        // Use the dragged entry directly
        const draggedEntry = draggedItemInfo.entry;

        let availabilityStatus: AvailabilityStatus = {
            teacherStatus: 'red',
            groupStatus: 'red',
            isRoomOccupied: false,
            isRoomValidForSubject: false,
            overallStatus: 'red',
            conflictReasons: [{ reason: 'Dragged item not found.', severity: 'red' }],
            conflictReason: 'Dragged item not found.'
        };

        if (draggedEntry && targetRoom) {
            const targetRoomName = targetRoom.name || targetRoom.roomCode || `Room ${targetRoom.id}`;
            const result = availabilityService.checkAvailability(
                draggedEntry, targetRoomName, day, targetTimeMinutes,
                baseScheduleEntries, teachers, groups, subjects
            );
            availabilityStatus = {
                ...result,
                teacherStatus: result.teacherStatus as Severity,
                groupStatus: result.groupStatus as Severity,
                overallStatus: result.overallStatus as Severity,
                conflictReasons: result.conflictReasons.map(reason => ({
                    ...reason,
                    severity: reason.severity as Severity
                }))
            };
        } else {
            console.warn(`[${day}-Simple] handleDragOver: No dragged entry available.`);
        }

        setDragOverData({ roomIndex: targetRoomIndex, targetTimeMinutes, availabilityStatus });

        // Update Overlay Info
        const isUnavailable = availabilityStatus.overallStatus === 'red' || availabilityStatus.overallStatus === 'orange';
        if (isUnavailable && availabilityStatus.conflictReasons && availabilityStatus.conflictReasons.length > 0) {
            // Position the conflict overlay next to the dragged item
            // Calculate the position relative to the grid container for proper positioning
            const markerLeftInContent = gridTimeColWidth + (targetRoomIndex * effectiveRoomColumnWidth);
            const markerLeftInViewport = markerLeftInContent - scrollLeft;

            // Calculate the center of the target time slot for better vertical alignment
            const markerTopPx = targetTopPx + gridHeaderHeight + gridPadding;
            const timeSlotHeight = (totalGridContentHeight * (draggedItemInfo.durationMinutes || 60)) / TOTAL_SCHEDULE_MINUTES;
            const centerY = markerTopPx + (timeSlotHeight / 2);

            // Position overlay to the right of the target column with some offset
            // Check if there's enough space on the right, otherwise position on the left
            const rightSpace = window.innerWidth - (gridRect.left + markerLeftInViewport + effectiveRoomColumnWidth + 350); // 350px for overlay width
            const overlayLeft = rightSpace > 0
                ? markerLeftInViewport + effectiveRoomColumnWidth + 15 // Position on the right
                : markerLeftInViewport - 350 - 15; // Position on the left if not enough space
            const overlayTop = centerY - 20; // Center vertically on the time slot, offset by 20px for better alignment

            setOverlayInfo({
                isVisible: true,
                reasons: availabilityStatus.conflictReasons,
                position: { top: overlayTop, left: overlayLeft }
            });
        } else {
            setOverlayInfo(prev => ({ ...prev, isVisible: false }));
        }

    }, [isAdmin, draggedItemInfo, rooms, day, totalGridContentHeight, baseScheduleEntries, gridHeaderHeight, gridTimeColWidth, teachers, groups, subjects, currentWeekNumber, currentYear, resizeState.isResizing]);

    const handleGridDragLeave = useCallback((e: React.DragEvent) => {
        if (!isAdmin) return;

        // Don't process drag leave if in resize mode
        if (resizeState.isResizing) return;

        if (gridRef.current && !gridRef.current.contains(e.relatedTarget as Node)) {
            setDragOverData(null);
            setOverlayInfo({ isVisible: false, reasons: [], position: { top: 0, left: 0 } });
        }
    }, [isAdmin, resizeState.isResizing]);

    // --- MODIFIED: Drop handler calls parent, parent handles confirmation ---
    const handleGridDrop = useCallback((e: React.DragEvent) => {
        console.log(`[${day}-Simple] handleGridDrop called, isAdmin:`, isAdmin);
        if (!isAdmin) {
            console.log(`[${day}-Simple] Drop rejected - not admin`);
            return;
        }

        // Prevent drop processing when in resize mode
        if (resizeState.isResizing) {
            console.log(`[${day}-Simple] Drop rejected - in resize mode`);
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const itemId = e.dataTransfer.getData('text/plain');
        console.log(`[${day}-Simple] Drop detected: itemId=${itemId}, dragOverData=`, dragOverData);
        console.log(`[${day}-Simple] Event target:`, e.target);
        console.log(`[${day}-Simple] Event currentTarget:`, e.currentTarget);

        setOverlayInfo({ isVisible: false, reasons: [], position: { top: 0, left: 0 } }); // Hide Overlay

        if (!itemId || !dragOverData || !baseScheduleEntries) {
            console.error(`[${day}-Simple] Drop failed: Missing data.`, { itemId, dragOverData, baseScheduleEntriesCount: baseScheduleEntries?.length });
            setDragOverData(null);
            onGlobalDragEnd(); // Ensure parent state is cleared
            return;
        }

        const targetRoom = rooms[dragOverData.roomIndex];
        const finalTargetStartMinute = dragOverData.targetTimeMinutes;
        const newStartTimeString = formatTimeFromMinutes(finalTargetStartMinute);

        // Add null check for targetRoom
        if (!targetRoom) {
            console.error(`[${day}-Simple] No target room found for index ${dragOverData.roomIndex}`);
            onGlobalDragEnd(); // Ensure parent state is cleared
            return;
        }

        console.log(`[${day}-Simple] Calling parent drop handler: itemId=${itemId}, targetRoomId=${targetRoom.id}, targetRoomName=${targetRoom.name}, day=${day}, time=${newStartTimeString}`);

        // Call the parent's drop handler - IT WILL HANDLE CONFIRMATION
        if (onItemDrop && targetRoom && newStartTimeString) {
            onItemDrop(itemId, targetRoom.id, day, newStartTimeString);
        } else {
            console.error(`[${day}-Simple] Failed to trigger parent drop handler.`, {
                onItemDrop: !!onItemDrop,
                targetRoom,
                newStartTimeString
            });
            onGlobalDragEnd(); // Ensure parent state is cleared if drop handler fails
        }

        setDragOverData(null); // Clear local state after attempting drop

    }, [isAdmin, dragOverData, rooms, day, onItemDrop, onGlobalDragEnd, baseScheduleEntries]);

    // --- Click Handler ---
    const handleItemClick = useCallback((e: React.MouseEvent, entry: ScheduleEntry) => {
        if ((e.target as Element).closest('.conflict-icon-span')) { return; } // Ignore clicks on icon

        // Prevent default card click behavior during resize mode or immediately after resize (Requirement 2.3)
        if (resizeState.isResizing || recentlyResizedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        const itemData = entry instanceof ScheduleEntry ? entry : ScheduleEntry.fromObject(entry);

        if (isAdmin) {
            if (onOpenModal) {
                onOpenModal(itemData);
            }
        } else {
            setDetailEntry(itemData);
            setIsDetailOpen(true);
        }
    }, [onOpenModal, isAdmin, resizeState.isResizing]);

    // --- Context Menu Handlers ---
    const handleEditItem = useCallback((entry: ScheduleEntry) => {
        if (isAdmin && onOpenModal) {
            onOpenModal(entry);
        }
    }, [isAdmin, onOpenModal]);

    const handleCopyItem = useCallback((entry: ScheduleEntry) => {
        if (onCopyItem) {
            onCopyItem(entry);
        }
    }, [onCopyItem]);

    const handleDuplicateItem = useCallback((entry: ScheduleEntry) => {
        if (onDuplicateItem) {
            onDuplicateItem(entry);
        }
    }, [onDuplicateItem]);

    const handleDeleteItem = useCallback((entry: ScheduleEntry) => {
        if (onDelete) {
            onDelete(entry);
        }
    }, [onDelete]);

    // Resize event handlers implementation
    const handleResizeStart = useCallback((itemId: string, edge: 'top' | 'bottom', mouseY: number) => {
        console.log(`[${day}-Simple] Resize start:`, { itemId, edge, mouseY });

        // Find the schedule entry being resized
        const entry = scheduleEntries.find(e => e.id === itemId);
        if (!entry) {
            console.warn(`[${day}-Simple] Could not find entry with id: ${itemId}`);
            return;
        }

        // Start the resize operation with the entry's current times
        resizeHandlers.onResizeStart(itemId, edge, mouseY, entry.timeStart, entry.timeEnd);

        // Initialize tooltip (Requirement 5.2)
        if (gridRef.current) {
            const gridRect = gridRef.current.getBoundingClientRect();
            const tooltipX = gridRect.left + gridRect.width / 2;
            const tooltipY = mouseY - 10;

            setResizeTooltip({
                visible: true,
                position: { x: tooltipX, y: tooltipY },
                violations: []
            });
        }
    }, [day, scheduleEntries, resizeHandlers]);

    const handleResizeMove = useCallback((mouseY: number) => {
        if (!resizeState.isResizing) return;

        // Pass the total grid content height to the resize handler
        resizeHandlers.onResizeMove(mouseY, totalGridContentHeight);

        // Update tooltip position and content (Requirement 5.2)
        if (gridRef.current) {
            const gridRect = gridRef.current.getBoundingClientRect();
            const tooltipX = gridRect.left + gridRect.width / 2;
            const tooltipY = mouseY - 10; // Position slightly above cursor

            // Check for constraint violations
            const violations: string[] = [];
            const currentStartMinutes = timeToMinutes(resizeState.currentStartTime);
            const currentEndMinutes = timeToMinutes(resizeState.currentEndTime);

            if (currentStartMinutes !== null && currentEndMinutes !== null) {
                if (currentStartMinutes < SCHEDULE_START_MINUTE) {
                    violations.push('Start time before schedule bounds');
                }
                if (currentEndMinutes > SCHEDULE_END_MINUTE) {
                    violations.push('End time after schedule bounds');
                }
                if (currentEndMinutes - currentStartMinutes < 5) {
                    violations.push('Duration too short (minimum 5 minutes)');
                }
            }

            setResizeTooltip({
                visible: true,
                position: { x: tooltipX, y: tooltipY },
                violations
            });
        }
    }, [resizeState.isResizing, resizeHandlers, totalGridContentHeight, resizeState.currentStartTime, resizeState.currentEndTime]);

    const handleResizeEnd = useCallback(() => {
        if (!resizeState.isResizing) return;

        console.log(`[${day}-Simple] Resize end - applying changes:`, {
            itemId: resizeState.itemId,
            newStartTime: resizeState.currentStartTime,
            newEndTime: resizeState.currentEndTime
        });

        // Set flag to prevent clicks immediately after resize
        recentlyResizedRef.current = true;

        // Hide tooltip (Requirement 5.2)
        setResizeTooltip({
            visible: false,
            position: { x: 0, y: 0 },
            violations: []
        });

        // Apply the time changes to the schedule entry (Requirements 8.1, 8.4)
        if (resizeState.itemId && onItemResize) {
            onItemResize(resizeState.itemId, resizeState.currentStartTime, resizeState.currentEndTime);
        }

        resizeHandlers.onResizeEnd();

        // Clear the flag after a short delay to allow clicks again
        setTimeout(() => {
            recentlyResizedRef.current = false;
        }, 100); // 100ms delay should be enough to prevent accidental clicks
    }, [day, resizeState, resizeHandlers, onItemResize]);

    const handleResizeCancel = useCallback(() => {
        if (!resizeState.isResizing) return;

        console.log(`[${day}-Simple] Resize cancelled`);

        // Set flag to prevent clicks immediately after resize cancel
        recentlyResizedRef.current = true;

        // Hide tooltip (Requirement 5.2)
        setResizeTooltip({
            visible: false,
            position: { x: 0, y: 0 },
            violations: []
        });

        resizeHandlers.onResizeCancel();

        // Clear the flag after a short delay to allow clicks again
        setTimeout(() => {
            recentlyResizedRef.current = false;
        }, 100); // 100ms delay should be enough to prevent accidental clicks
    }, [resizeState.isResizing, resizeHandlers]);

    // --- Drag-to-Create Handlers ---
    const handleMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
        // Only proceed if admin and not dragging an existing item
        if (!isAdmin || draggedItemInfo.entry || !onDragToCreateStart || !gridRef.current) return;

        // Prevent drag-to-create when in resize mode
        if (resizeState.isResizing) return;

        // Check if we're clicking on an empty area (not on a schedule item)
        if ((e.target as Element).closest('[data-type="scheduled_item"]')) return;

        // Also check if we're clicking on any interactive element (buttons, icons, etc.)
        if ((e.target as Element).closest('button, .conflict-icon-span, [draggable="true"]')) return;

        console.log(`[${day}-Simple] Mouse down for potential drag-to-create, draggedItemInfo.entry:`, draggedItemInfo.entry);

        const gridRect = gridRef.current.getBoundingClientRect();
        const cursorX = e.clientX - gridRect.left;
        const cursorY = e.clientY - gridRect.top;

        // Calculate which room column was clicked
        const scrollLeft = gridRef.current.scrollLeft || 0;
        const cursorXInContent = cursorX + scrollLeft;
        const visibleRoomAreaWidth = gridRect.width - gridTimeColWidth;
        const averageDynamicWidthIfViewportConstrained = visibleRoomAreaWidth > 0 ? visibleRoomAreaWidth / rooms.length : 1;
        const effectiveRoomColumnWidth = Math.max(120, averageDynamicWidthIfViewportConstrained);

        let targetRoomIndex = Math.floor((cursorXInContent - gridTimeColWidth) / effectiveRoomColumnWidth);
        targetRoomIndex = Math.max(0, Math.min(targetRoomIndex, rooms.length - 1));
        const targetRoom = rooms[targetRoomIndex];

        // Add null check for targetRoom
        if (!targetRoom) return;

        // Calculate which time was clicked
        const contentCursorY = Math.max(0, cursorY - gridHeaderHeight);
        const minutesOffset = (contentCursorY / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
        let targetTimeMinutes = SCHEDULE_START_MINUTE + minutesOffset;

        // Round to configured time intervals
        targetTimeMinutes = Math.round(targetTimeMinutes / DRAG_CONFIG.timeSnapIntervalMinutes) * DRAG_CONFIG.timeSnapIntervalMinutes;
        targetTimeMinutes = Math.max(SCHEDULE_START_MINUTE, Math.min(targetTimeMinutes, SCHEDULE_END_MINUTE - DRAG_CONFIG.timeSnapIntervalMinutes));

        const startPosition = { x: cursorX, y: cursorY };

        // Store the initial drag data but don't start dragging yet
        setDragStartPosition(startPosition);
        setDragStartData({
            day,
            room: targetRoom,
            startTimeMinutes: targetTimeMinutes,
            position: startPosition
        });

        console.log('[DayTableSimple] Mouse down - preparing for potential drag-to-create:', {
            day, targetRoomId: targetRoom.id, targetRoomName: targetRoom.name, targetTimeMinutes
        });

        // Prevent text selection during potential drag
        e.preventDefault();
    }, [isAdmin, draggedItemInfo.entry, onDragToCreateStart, rooms, day, gridHeaderHeight, totalGridContentHeight, gridTimeColWidth]);

    const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
        // Only handle drag-to-create if we're admin and NOT dragging an existing item
        if (!isAdmin || !onDragToCreateMove || !gridRef.current || draggedItemInfo.entry) return;

        // Don't handle drag-to-create when in resize mode
        if (resizeState.isResizing) return;

        const gridRect = gridRef.current.getBoundingClientRect();
        const cursorX = e.clientX - gridRect.left;
        const cursorY = e.clientY - gridRect.top;

        // If we have start data but haven't started dragging yet, check if we should start
        if (dragStartData && !isDraggingToCreate && dragStartPosition) {
            const deltaX = Math.abs(cursorX - dragStartPosition.x);
            const deltaY = Math.abs(cursorY - dragStartPosition.y);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (distance >= DRAG_THRESHOLD) {
                // Start the actual drag-to-create process
                console.log('[DayTableSimple] Starting drag-to-create after threshold exceeded');
                setIsDraggingToCreate(true);
                if (onDragToCreateStart) {
                    onDragToCreateStart(
                        dragStartData.day,
                        dragStartData.room.id,
                        dragStartData.startTimeMinutes,
                        dragStartData.position,
                        currentWeekNumber,
                        currentYear
                    );
                }
            }
        }

        // If we're already dragging to create, continue with the move logic
        if (isDraggingToCreate && dragStartPosition) {
            // Calculate current time based on mouse position
            const contentCursorY = Math.max(0, cursorY - gridHeaderHeight);
            const minutesOffset = (contentCursorY / totalGridContentHeight) * TOTAL_SCHEDULE_MINUTES;
            let currentTimeMinutes = SCHEDULE_START_MINUTE + minutesOffset;

            // Round to configured time intervals
            currentTimeMinutes = Math.round(currentTimeMinutes / DRAG_CONFIG.timeSnapIntervalMinutes) * DRAG_CONFIG.timeSnapIntervalMinutes;
            currentTimeMinutes = Math.max(SCHEDULE_START_MINUTE, Math.min(currentTimeMinutes, SCHEDULE_END_MINUTE));

            const currentPosition = { x: cursorX, y: cursorY };

            // Call parent handler
            onDragToCreateMove(currentPosition, currentTimeMinutes);
        }
    }, [isAdmin, isDraggingToCreate, onDragToCreateMove, dragStartPosition, dragStartData, gridHeaderHeight, totalGridContentHeight, onDragToCreateStart, DRAG_THRESHOLD, draggedItemInfo.entry, currentWeekNumber, currentYear, resizeState.isResizing]);

    const handleMouseUp = useCallback(() => {
        // Only handle drag-to-create cleanup if we're admin and not dragging an existing item
        if (!isAdmin || draggedItemInfo.entry) return;

        // Don't handle drag-to-create cleanup when in resize mode
        if (resizeState.isResizing) return;

        // If we were dragging, end the drag
        if (isDraggingToCreate && onDragToCreateEnd) {
            console.log('[DayTableSimple] Mouse up for drag-to-create');
            onDragToCreateEnd();
        } else if (dragStartData) {
            // If we had start data but never started dragging, it was just a click
            console.log('[DayTableSimple] Mouse up - was just a click, not creating entry');
        }

        // Clean up all drag-to-create state
        setIsDraggingToCreate(false);
        setDragStartPosition(null);
        setDragStartData(null);
    }, [isAdmin, isDraggingToCreate, onDragToCreateEnd, dragStartData, draggedItemInfo.entry, resizeState.isResizing]);

    const handleMouseLeave = useCallback(() => {
        // Only handle drag-to-create cleanup if we're admin and not dragging an existing item
        if (!isAdmin || draggedItemInfo.entry) return;

        // Don't handle drag-to-create cleanup when in resize mode
        if (resizeState.isResizing) return;

        // If we were dragging, cancel the drag
        if (isDraggingToCreate && onDragToCreateCancel) {
            console.log('[DayTableSimple] Mouse leave - canceling drag-to-create');
            onDragToCreateCancel();
        } else if (dragStartData) {
            // If we had start data but never started dragging, just clean up
            console.log('[DayTableSimple] Mouse leave - cleaning up click state');
        }

        // Clean up all drag-to-create state
        setIsDraggingToCreate(false);
        setDragStartPosition(null);
        setDragStartData(null);
    }, [isAdmin, isDraggingToCreate, onDragToCreateCancel, dragStartData, draggedItemInfo.entry, resizeState.isResizing]);

    // --- Generate Time Labels (Keep as is) ---
    const timeLabels = useMemo(() => {
        const labels: TimeLabel[] = [];
        for (let i = 0; i < numberOfTimeRows; i++) {
            const minutes = SCHEDULE_START_MINUTE + i * timeIntervalMinutes;
            if (minutes % 60 === 0 || i === 0) {
                labels.push({ time: formatTimeFromMinutes(minutes), row: Math.floor((minutes - SCHEDULE_START_MINUTE) / timeIntervalMinutes) + 2 });
            }
        }
        return labels;
    }, [numberOfTimeRows, timeIntervalMinutes]);

    // Effect to call the ref callback
    useEffect(() => {
        if (dayHeaderRef && dayHeaderElementRef.current) {
            dayHeaderRef(dayHeaderElementRef.current);
        }
    }, [dayHeaderRef]); // Remove dayHeaderElementRef from dependencies

    // Cleanup effect for shared scroll registration
    useEffect(() => {
        return () => {
            if (gridRef.current) {
                unregisterScrollRef(gridRef.current);
            }
        };
    }, [unregisterScrollRef]);

    // Also use a callback ref as a backup to ensure the ref is always set
    const handleHeaderRef = useCallback((element: HTMLHeadingElement | null) => {
        dayHeaderElementRef.current = element;
        if (dayHeaderRef && element) {
            dayHeaderRef(element);
        }
    }, [dayHeaderRef]);

    // Global mouse event handlers for resize operations
    useEffect(() => {
        if (!resizeState.isResizing) return;

        const handleGlobalMouseMove = (e: globalThis.MouseEvent) => {
            e.preventDefault();
            handleResizeMove(e.clientY);
        };

        const handleGlobalMouseUp = (e: globalThis.MouseEvent) => {
            e.preventDefault();
            handleResizeEnd();
        };

        // Add global event listeners
        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('mouseup', handleGlobalMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [resizeState.isResizing, handleResizeMove, handleResizeEnd]);

    // Global keyboard event handlers for resize operations
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            handleKeyDown(e);
        };

        const handleGlobalKeyUp = (e: KeyboardEvent) => {
            handleKeyUp(e);
        };

        // Add global keyboard event listeners
        document.addEventListener('keydown', handleGlobalKeyDown);
        document.addEventListener('keyup', handleGlobalKeyUp);

        return () => {
            document.removeEventListener('keydown', handleGlobalKeyDown);
            document.removeEventListener('keyup', handleGlobalKeyUp);
        };
    }, [handleKeyDown, handleKeyUp]);

    // Calculate day info for header display
    const dayInfo = useMemo((): DayInfo => {
        // Only show day numbers when we have a specific week selected (week view)
        if (currentWeekNumber && currentYear) {
            const weekDateRange = getWeekDateRange(currentYear, currentWeekNumber);
            if (weekDateRange) {
                const formatted = formatDayWithNumber(day, weekDateRange.startDate);
                return {
                    shortName: formatted.shortName,
                    dayNumber: formatted.dayNumber,
                    fullDate: formatted.fullDate
                };
            }
        }

        // For period/month/whole-year views, only show the day name without numbers
        const dayMapping: { [key: string]: string } = {
            'monday': 'Mon',
            'tuesday': 'Tue',
            'wednesday': 'Wed',
            'thursday': 'Thu',
            'friday': 'Fri',
            'saturday': 'Sat',
            'sunday': 'Sun'
        };

        return {
            shortName: dayMapping[day.toLowerCase()] || day.slice(0, 3),
            dayNumber: null, // No day number for non-week views
            fullDate: null
        };
    }, [day, currentWeekNumber, currentYear]);

    // --- Render Component ---
    return (
        <Card className="overflow-hidden shadow-xl rounded-lg border-2 border-gray-200" onClick={() => { }}
            onDrop={isAdmin ? (e: React.DragEvent) => {
                console.log(`[${day}-Simple] Card-level drop event detected`);
                // Let it bubble to the grid handler
            } : undefined}>
            {(showMondayHeader || day !== activeDays[0]) && (
                <h2
                    ref={handleHeaderRef}
                    className="top-0 w-full text-xl font-bold tracking-wide py-3 px-4 capitalize border-b-2 bg-gradient-to-r from-grey-50 to-white text-grey-700 shadow-lg overflow-hidden"
                >
                    <span>{dayInfo.shortName}</span>
                    {dayInfo.dayNumber !== null && (
                        <span className="text-lg font-normal text-slate-800 opacity-90 ml-1">
                            {dayInfo.dayNumber}
                        </span>
                    )}
                </h2>
            )}

            <div
                ref={combinedRef}
                className="relative overflow-auto py-2 bg-gradient-to-br from-gray-50 to-white w-full"
                style={{
                    display: 'grid',
                    gridTemplateColumns: `${gridTimeColWidth}px repeat(${rooms.length}, minmax(120px, 1fr))`,
                    gridTemplateRows: `${gridHeaderHeight}px repeat(${numberOfTimeRows}, ${rowHeightPx}px)`,
                    minHeight: `${gridHeaderHeight + totalGridContentHeight}px`,
                    maxWidth: '100%',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onDragOver={handleGridDragOver}
                onDrop={handleGridDrop}
                onDragLeave={handleGridDragLeave}
                onScroll={(e) => {
                    const target = e.target as HTMLDivElement;
                    if (target && typeof target.scrollLeft === 'number') {
                        // Debounce the scroll update to prevent excessive calls
                        const scrollLeft = target.scrollLeft;
                        if (Math.abs(scrollLeft - sharedScrollLeft) > 1) { // Only update if change is significant
                            updateSharedScroll(scrollLeft);
                        }
                    }
                }}
            >
                {/* Time Labels */}
                {timeLabels.map(label => (
                    <div key={`time-${label.time}`} className="text-right text-xs font-semibold text-gray-700 pr-3 border-r-2 border-t border-gray-300 sticky left-0 bg-white z-20 flex items-start justify-end" style={{ gridColumn: 1, gridRowStart: label.row, gridRowEnd: `span ${Math.ceil(60 / timeIntervalMinutes)}`, paddingTop: '4px' }}>
                        <span className="bg-white px-2 py-1 rounded text-gray-700 font-medium">{label.time}</span>
                    </div>
                ))}
                {/* Room Headers */}
                <div className="sticky top-0 left-0 z-30 bg-gradient-to-b from-gray-50 to-gray-100 border-b-2 border-r-2 border-gray-300" style={{ gridColumn: 1, gridRow: 1 }}></div>
                {rooms.map((room, index) => (
                    <div key={`room-header-${room.id}`} className="p-2 border-b-2 border-r border-gray-300 text-center text-sm font-medium text-gray-800 whitespace-nowrap sticky top-0 z-20 bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center tracking-wide" style={{ gridColumn: index + 2, gridRow: 1 }}>
                        <span className="truncate">{room.name || room.roomCode || `Room ${room.id}`}</span>
                    </div>
                ))}
                {/* Background Grid Lines */}
                {Array.from({ length: numberOfTimeRows }).map((_, rowIndex) => (<div key={`hline-${rowIndex}`} className="border-t border-gray-200 pointer-events-none opacity-60" style={{ gridRow: rowIndex + 2, gridColumn: `2 / span ${rooms.length}`, zIndex: 1 }} ></div>))}
                {Array.from({ length: rooms.length }).map((_, colIndex) => (<div key={`vline-${colIndex}`} className="border-l border-gray-200 pointer-events-none opacity-60" style={{ gridColumn: colIndex + 2, gridRow: `2 / span ${numberOfTimeRows}`, zIndex: 1 }} ></div>))}

                {/* Availability Overlays */}
                <AvailabilityTransparentOverlays
                    isAdmin={isAdmin}
                    availabilityOverlayData={availabilityOverlayData}
                    day={day}
                    roomIndexMap={roomIndexMap}
                    totalGridContentHeight={totalGridContentHeight}
                    gridHeaderHeight={gridHeaderHeight}
                    checkIntervalMinutes={checkIntervalMinutes}
                    KEY_SEPARATOR={KEY_SEPARATOR}
                />

                {/* Holiday Overlay */}
                {holiday && (
                    <div
                        className="absolute"
                        style={{
                            top: `${gridHeaderHeight + gridPadding}px`,
                            left: `${gridTimeColWidth}px`,
                            right: '0px',
                            height: `${totalGridContentHeight}px`,
                            zIndex: 5
                        }}
                    >
                        <HolidayOverlay holiday={holiday} />
                    </div>
                )}

                {/* Schedule Items */}
                {scheduleEntries.map(entry => {
                    const style = getItemStyle(entry);
                    if ((style as any).display === 'none') return null;

                    // Check if this entry should be highlighted
                    const isHighlighted = highlightedItemId !== null && String(entry.id) === String(highlightedItemId);

                    return (
                        <ScheduleItemCard
                            key={entry.id}
                            entry={entry}
                            isAdmin={isAdmin}
                            style={style as React.CSSProperties}
                            formatGroupNames={getGroupDisplayNames}
                            onDragStart={(e) => handleItemDragStart(e as any, entry)}
                            onDragEnd={handleItemDragEnd as any}
                            onClick={(e) => handleItemClick(e as any, entry)}
                            onConflictIconClick={(e, reasons) => handleIconClick(e, entry, reasons)}
                            onEdit={isAdmin ? handleEditItem : undefined}
                            onCopy={onCopyItem ? handleCopyItem : undefined}
                            onDuplicate={onDuplicateItem ? handleDuplicateItem : undefined}
                            onDelete={onDelete ? handleDeleteItem : undefined}
                            onResizeStart={isAdmin ? handleResizeStart : undefined}
                            isSaveAnimationActive={isSaveAnimationActive}
                            isHighlighted={isHighlighted}
                        />
                    );
                })}

                {/* Drag Over Marker */}
                {isAdmin && dragOverData && draggedItemInfo.entry && (
                    <>
                        {/* Time Display */}
                        <div className="absolute pointer-events-none" style={{ gridColumnStart: dragOverData.roomIndex + 2, gridColumnEnd: dragOverData.roomIndex + 3, top: `${(((dragOverData.targetTimeMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight + gridPadding}px`, transform: 'translateY(-100%) translateY(-4px)', zIndex: 51, width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <span className={`text-xs font-bold text-white px-3 py-1.5 rounded-lg shadow-lg border-2 border-white/20 ${dragOverData.availabilityStatus?.overallStatus === 'red' ? 'bg-red-600' : dragOverData.availabilityStatus?.overallStatus === 'orange' ? 'bg-orange-500' : 'bg-blue-600'}`}>
                                {formatTimeFromMinutes(dragOverData.targetTimeMinutes)} - {formatTimeFromMinutes(dragOverData.targetTimeMinutes + (draggedItemInfo.durationMinutes || 0))}
                            </span>
                        </div>
                        {/* Availability Marker Box */}
                        <div
                            className={`absolute border-2 rounded-lg pointer-events-none opacity-80 ${(() => {
                                const status = dragOverData.availabilityStatus?.overallStatus; const reasons = dragOverData.availabilityStatus?.conflictReasons || [];
                                const isOnlyRoomOccupancy = reasons.length === 1 && reasons[0]?.reason?.startsWith('Room (') && reasons[0]?.reason?.includes(' occupied by ');
                                if (status === 'red' && isOnlyRoomOccupancy) return 'border-blue-500 border-dashed bg-blue-300/40 shadow-lg';
                                if (status === 'red') return 'border-red-600 bg-red-400/60 shadow-lg';
                                if (status === 'orange') return 'border-orange-500 bg-orange-300/60 shadow-lg';
                                return 'border-blue-500 border-dashed bg-blue-300/40 shadow-lg';
                            })()}`}
                            title={dragOverData.availabilityStatus?.conflictReason || 'Slot appears available'}
                            style={{ top: `${(((dragOverData.targetTimeMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight + gridPadding}px`, height: `calc(${totalGridContentHeight}px * ${(draggedItemInfo.durationMinutes || 0) / TOTAL_SCHEDULE_MINUTES})`, gridColumnStart: dragOverData.roomIndex + 2, gridColumnEnd: dragOverData.roomIndex + 3, width: '100%', zIndex: 50 }}
                        ></div>
                    </>
                )}

                {/* Availability Overlay (Positioned relative to the grid) */}
                <AvailabilityOverlay
                    reasons={overlayInfo.reasons.map(reason => ({
                        reason: reason.reason,
                        severity: reason.severity
                    }))}
                    position={overlayInfo.position}
                    isVisible={overlayInfo.isVisible}
                />

                {/* Drag-to-Create Preview */}
                {isAdmin && dragToCreateData?.isActive && dragToCreateData.day === day && dragToCreateData.startTime !== null && dragToCreateData.endTime !== null && dragToCreateData.roomId && (
                    <>
                        {(() => {
                            // Find the room object by ID and get its name
                            const targetRoom = rooms.find(room => room.id === dragToCreateData.roomId);
                            const roomName = targetRoom?.name || targetRoom?.roomCode || `Room ${dragToCreateData.roomId}`;
                            const roomIndex = roomIndexMap.get(roomName);
                            if (roomIndex === undefined) return null;

                            const startTime = dragToCreateData.startTime;
                            const endTime = dragToCreateData.endTime;
                            const duration = endTime - startTime;

                            const topPosition = (((startTime - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight + gridPadding;
                            const height = (totalGridContentHeight * duration / TOTAL_SCHEDULE_MINUTES);

                            return (
                                <>
                                    {/* Time Display */}
                                    <div
                                        className="absolute pointer-events-none"
                                        style={{
                                            gridColumnStart: roomIndex + 2,
                                            gridColumnEnd: roomIndex + 3,
                                            top: `${topPosition}px`,
                                            transform: 'translateY(-100%) translateY(-4px)',
                                            zIndex: 51,
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <span className="text-xs font-bold text-white px-3 py-1.5 rounded-lg shadow-lg border-2 border-white/20 bg-green-600">
                                            {formatTimeFromMinutes(startTime)} - {formatTimeFromMinutes(endTime)}
                                        </span>
                                    </div>

                                    {/* Preview Box */}
                                    <div
                                        className="absolute border-2 border-green-600 bg-green-200/50 rounded-lg pointer-events-none opacity-90 flex items-center justify-center shadow-lg"
                                        style={{
                                            top: `${topPosition}px`,
                                            height: `${Math.max(20, height)}px`,
                                            gridColumnStart: roomIndex + 2,
                                            gridColumnEnd: roomIndex + 3,
                                            width: '100%',
                                            zIndex: 50
                                        }}
                                    >
                                        <span className="text-sm font-bold text-green-800 bg-white/90 px-3 py-1.5 rounded-lg shadow-md border border-green-300">
                                            ✨ New Entry
                                        </span>
                                    </div>
                                </>
                            );
                        })()}
                    </>
                )}

                {/* Resize Time Display - Show time range above card during resize */}
                {resizeState.isResizing && resizeState.itemId && (
                    (() => {
                        // Find the entry being resized to get its room and position
                        const resizingEntry = scheduleEntries.find(entry => entry.id === resizeState.itemId);
                        if (!resizingEntry || !resizingEntry.room) return null;

                        const roomIndex = roomIndexMap.get(resizingEntry.room);
                        if (roomIndex === undefined) return null;

                        const currentStartMinutes = timeToMinutes(resizeState.currentStartTime) || 0;
                        const topPosition = (((currentStartMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight + gridPadding;

                        // Check if there are violations for color coding
                        const hasViolations = resizeTooltip.violations.length > 0;

                        return (
                            <div
                                className="absolute pointer-events-none"
                                style={{
                                    gridColumnStart: roomIndex + 2,
                                    gridColumnEnd: roomIndex + 3,
                                    top: `${topPosition}px`,
                                    transform: 'translateY(-100%) translateY(-4px)',
                                    zIndex: 51,
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <span className={`text-xs font-bold text-white px-3 py-1.5 rounded-lg shadow-lg border-2 border-white/20 ${hasViolations ? 'bg-red-600' : 'bg-blue-600'
                                    }`}>
                                    {resizeState.currentStartTime} - {resizeState.currentEndTime}
                                </span>
                            </div>
                        );
                    })()
                )}

                {/* Resize Grid Guides - Visual alignment guides during resize (Requirement 5.3) */}
                <ResizeGridGuides
                    visible={resizeState.isResizing}
                    currentStartMinutes={timeToMinutes(resizeState.currentStartTime) || 0}
                    currentEndMinutes={timeToMinutes(resizeState.currentEndTime) || 0}
                    totalGridContentHeight={totalGridContentHeight}
                    gridHeaderHeight={gridHeaderHeight}
                    gridPadding={gridPadding}
                    snapInterval={5}
                />

            </div> {/* End Grid Container */}

            {/* Resize Tooltip - Time display during resize (Requirement 5.2)
            <ResizeTooltip
                visible={resizeTooltip.visible}
                startTime={resizeState.currentStartTime}
                endTime={resizeState.currentEndTime}
                position={resizeTooltip.position}
                violations={resizeTooltip.violations}
            /> */}

            {/* Popover for static item conflicts */}
            {activePopover.itemId && (
                <Popover popoverRef={popoverRef} reasons={activePopover.reasons} position={activePopover.position} onClose={() => setActivePopover({ itemId: null, reasons: [], position: { x: 0, y: 0 } })} />
            )}

            {/* Read-only details for public users */}
            {!isAdmin && (
                <ScheduleEntryDetailView
                    isOpen={isDetailOpen}
                    onClose={() => setIsDetailOpen(false)}
                    scheduleEntry={detailEntry}
                    groups={groups as any}
                    subjects={subjects as any}
                    teachers={teachers as any}
                    isPublic
                />
            )}
        </Card>
    );
}

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: DayTableSimpleProps, nextProps: DayTableSimpleProps) => {
    // Quick reference equality checks for primitive values and functions
    if (
        prevProps.day !== nextProps.day ||
        prevProps.KEY_SEPARATOR !== nextProps.KEY_SEPARATOR ||
        prevProps.currentWeekNumber !== nextProps.currentWeekNumber ||
        prevProps.currentYear !== nextProps.currentYear ||
        prevProps.user?.role !== nextProps.user?.role ||
        prevProps.onOpenModal !== nextProps.onOpenModal ||
        prevProps.onItemDrop !== nextProps.onItemDrop ||
        prevProps.onItemResize !== nextProps.onItemResize ||
        prevProps.onGlobalDragStart !== nextProps.onGlobalDragStart ||
        prevProps.onGlobalDragEnd !== nextProps.onGlobalDragEnd ||
        prevProps.dayHeaderRef !== nextProps.dayHeaderRef ||
        prevProps.onDragToCreateStart !== nextProps.onDragToCreateStart ||
        prevProps.onDragToCreateMove !== nextProps.onDragToCreateMove ||
        prevProps.onDragToCreateEnd !== nextProps.onDragToCreateEnd ||
        prevProps.onDragToCreateCancel !== nextProps.onDragToCreateCancel ||
        prevProps.onCopyItem !== nextProps.onCopyItem ||
        prevProps.onDuplicateItem !== nextProps.onDuplicateItem ||
        prevProps.showMondayHeader !== nextProps.showMondayHeader
    ) {
        return false;
    }

    // Array comparisons - check length first, then shallow comparison
    if (
        prevProps.rooms.length !== nextProps.rooms.length ||
        !prevProps.rooms.every((room, index) => room === nextProps.rooms[index])
    ) {
        return false;
    }

    if (
        prevProps.items.length !== nextProps.items.length ||
        !prevProps.items.every((item, index) => {
            const nextItem = nextProps.items[index];
            return item?.id === nextItem?.id &&
                item?.day === nextItem?.day &&
                item?.timeStart === nextItem?.timeStart &&
                item?.timeEnd === nextItem?.timeEnd &&
                item?.room === nextItem?.room &&
                item?.teacher === nextItem?.teacher &&
                item?.course === nextItem?.course;
        })
    ) {
        return false;
    }

    if (
        prevProps.dayScheduleEntry.length !== nextProps.dayScheduleEntry.length ||
        !prevProps.dayScheduleEntry.every((item, index) => {
            const nextItem = nextProps.dayScheduleEntry[index];
            return item?.id === nextItem?.id &&
                item?.day === nextItem?.day &&
                item?.timeStart === nextItem?.timeStart &&
                item?.timeEnd === nextItem?.timeEnd &&
                item?.room === nextItem?.room &&
                item?.teacher === nextItem?.teacher &&
                item?.course === nextItem?.course;
        })
    ) {
        return false;
    }

    if (
        prevProps.teachers.length !== nextProps.teachers.length ||
        !prevProps.teachers.every((teacher, index) => teacher?.id === nextProps.teachers[index]?.id)
    ) {
        return false;
    }

    if (
        prevProps.groups.length !== nextProps.groups.length ||
        !prevProps.groups.every((group, index) => group?.id === nextProps.groups[index]?.id)
    ) {
        return false;
    }

    if (
        prevProps.subjects.length !== nextProps.subjects.length ||
        !prevProps.subjects.every((subject, index) => subject?.id === nextProps.subjects[index]?.id)
    ) {
        return false;
    }

    // Drag and drop state comparison - only compare key properties that affect rendering
    if (
        prevProps.draggedItemInfo?.entry?.id !== nextProps.draggedItemInfo?.entry?.id ||
        prevProps.draggedItemInfo?.durationMinutes !== nextProps.draggedItemInfo?.durationMinutes ||
        prevProps.draggedItemInfo?.clickOffsetY !== nextProps.draggedItemInfo?.clickOffsetY
    ) {
        return false;
    }

    // Drag-to-create data comparison
    const prevDragToCreate = prevProps.dragToCreateData;
    const nextDragToCreate = nextProps.dragToCreateData;

    if (prevDragToCreate?.isActive !== nextDragToCreate?.isActive ||
        prevDragToCreate?.startTime !== nextDragToCreate?.startTime ||
        prevDragToCreate?.endTime !== nextDragToCreate?.endTime ||
        prevDragToCreate?.roomId !== nextDragToCreate?.roomId ||
        prevDragToCreate?.day !== nextDragToCreate?.day) {
        return false;
    }

    // Holiday comparison
    if (prevProps.holiday?.id !== nextProps.holiday?.id) {
        return false;
    }

    // Availability overlay data comparison (shallow check of Map size)
    if (prevProps.availabilityOverlayData?.size !== nextProps.availabilityOverlayData?.size) {
        return false;
    }

    // If all checks pass, props are considered equal
    return true;
};

export default React.memo(DayTableSimple, arePropsEqual);